import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  Globe2,
  Loader2,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Paragraph,
} from '@/components/atoms';
import { FormField } from '@/components/molecules';
import {
  useConnectCustomDomain,
  usePurchaseDomain,
  useRemoveCustomDomain,
  useSearchDomainAvailability,
  useStoreCustomDomain,
  useVerifyCustomDomain,
} from '@/api';
import {
  formatCurrency,
  getCustomDomainStatusPresentation,
  getCustomDomainUrl,
  isCustomDomainActive,
  needsDnsSetup,
  normalizeCustomDomainInput,
  validateCustomDomainFormat,
} from '@/lib/helpers';
import type {
  CustomDomain,
  CustomDomainVerifyResult,
  DomainDnsRecord,
  DomainSearchResult,
} from '@/types';
import { cn } from '@/lib/utils';

type DialogView = 'manage' | 'search' | 'review' | 'existing';

interface CustomDomainDialogProps {
  open: boolean;
  /** Store must exist before connecting a domain. */
  hasStore: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CustomDomainDialog = ({
  open,
  hasStore,
  onOpenChange,
}: CustomDomainDialogProps) => {
  const [view, setView] = useState<DialogView>('manage');
  const [domainInput, setDomainInput] = useState('');
  const [formatError, setFormatError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<DomainSearchResult | null>(
    null
  );
  const [ownsExistingDomain, setOwnsExistingDomain] = useState(false);
  const [verifyResult, setVerifyResult] =
    useState<CustomDomainVerifyResult | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    data: customDomain,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useStoreCustomDomain(open && hasStore);

  const {
    mutate: searchDomain,
    isPending: isSearching,
    reset: resetSearch,
  } = useSearchDomainAvailability();

  const { mutate: purchase, isPending: isPurchasing } = usePurchaseDomain(
    (data) => {
      if (data.data.checkoutUrl) {
        setIsRedirecting(true);
        window.location.href = data.data.checkoutUrl;
        return;
      }
      toast.error('Checkout URL was missing. Please try again.');
      setIsRedirecting(false);
    }
  );

  const { mutate: connect, isPending: isConnecting } = useConnectCustomDomain(
    () => {
      setView('manage');
      void refetch();
    }
  );

  const { mutate: verify, isPending: isVerifying } = useVerifyCustomDomain(
    (data) => {
      setVerifyResult(data.data);
      void refetch();
    }
  );

  const { mutate: remove, isPending: isRemoving } = useRemoveCustomDomain(
    () => {
      setVerifyResult(null);
      setView('search');
    }
  );

  const resetFlowState = () => {
    setDomainInput('');
    setFormatError(null);
    setSearchResult(null);
    setOwnsExistingDomain(false);
    setIsRedirecting(false);
    resetSearch();
  };

  useEffect(() => {
    if (!open) {
      setView('manage');
      setVerifyResult(null);
      resetFlowState();
      return;
    }

    if (!hasStore) {
      setView('manage');
      return;
    }

    if (!isLoading && !customDomain) {
      setView('search');
    } else {
      setView('manage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasStore, isLoading, customDomain]);

  const normalizedDomain = normalizeCustomDomainInput(domainInput);
  const isBusy =
    isSearching || isPurchasing || isConnecting || isRedirecting || isRemoving;

  const handleCheckAvailability = () => {
    const nextError = validateCustomDomainFormat(domainInput);
    setFormatError(nextError);
    setSearchResult(null);
    if (nextError) return;

    searchDomain(normalizedDomain, {
      onSuccess: (result) => setSearchResult(result),
    });
  };

  const handleContinueExisting = () => {
    const nextError = validateCustomDomainFormat(domainInput);
    setFormatError(nextError);
    if (nextError) return;
    if (!ownsExistingDomain) {
      setFormatError('Confirm that you already own this domain.');
      return;
    }
    connect({ domain: normalizedDomain });
  };

  const handlePurchase = () => {
    if (!searchResult?.available || isBusy) return;
    purchase(
      { domain: searchResult.domain },
      { onError: () => setIsRedirecting(false) }
    );
  };

  const handleCopy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const title =
    view === 'manage'
      ? 'Custom domain'
      : view === 'search'
        ? 'Find your custom domain'
        : view === 'existing'
          ? 'Connect an existing domain'
          : 'Review & purchase';

  const description =
    view === 'manage'
      ? 'Use your own web address for this store.'
      : view === 'search'
        ? 'Search for a domain to buy through Sellyt. We’ll handle registration after payment.'
        : view === 'existing'
          ? 'Enter a domain you already own. You’ll add DNS records, then verify.'
          : 'Review the price, then continue to Paystack checkout.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {!hasStore ? (
            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
              <Paragraph className="font-medium text-foreground">
                Save this store first
              </Paragraph>
              <Paragraph className="text-sm text-muted-foreground">
                Create your store, then open this again to buy or connect a
                custom domain.
              </Paragraph>
            </div>
          ) : null}

          {hasStore && view === 'manage' ? (
            <ManageView
              isLoading={isLoading}
              isError={isError}
              error={error}
              isFetching={isFetching}
              customDomain={customDomain}
              verifyResult={verifyResult}
              isVerifying={isVerifying}
              isRemoving={isRemoving}
              onRetryLoad={() => void refetch()}
              onStartNew={() => {
                resetFlowState();
                setView('search');
              }}
              onVerify={() => verify()}
              onRemove={() => remove()}
              onCopy={(value) => void handleCopy(value, 'Copied to clipboard!')}
              onOpen={(domain) =>
                window.open(
                  getCustomDomainUrl(domain),
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            />
          ) : null}

          {hasStore && view === 'search' ? (
            <>
              <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
                <Paragraph className="text-sm text-muted-foreground">
                  Buying a domain is a paid add-on. Availability and price come
                  from the registrar. Checkout is in Naira via Paystack.
                </Paragraph>
              </div>

              <FormField
                label="Preferred domain"
                error={formatError ?? undefined}
              >
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={domainInput}
                    placeholder="yourbrand.com"
                    disabled={isBusy}
                    onChange={(event) => {
                      setDomainInput(event.target.value);
                      setFormatError(null);
                      setSearchResult(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleCheckAvailability();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleCheckAvailability}
                    disabled={isBusy || !domainInput.trim()}
                    isLoading={isSearching}
                  >
                    {!isSearching && <Search className="size-4" />}
                    Check availability
                  </Button>
                </div>
              </FormField>

              <button
                type="button"
                className="text-sm text-primary hover:underline w-fit text-left"
                onClick={() => {
                  setSearchResult(null);
                  setFormatError(null);
                  setOwnsExistingDomain(false);
                  setView('existing');
                }}
              >
                Use existing domain instead?
              </button>

              {searchResult && (
                <div
                  className={cn(
                    'rounded-xl border p-4',
                    searchResult.available
                      ? 'border-primary/20 bg-primary/5'
                      : 'border-destructive/20 bg-destructive/5'
                  )}
                >
                  <div className="flex items-start gap-3">
                    {searchResult.available ? (
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="size-5 text-destructive shrink-0 mt-0.5" />
                    )}
                    <div className="space-y-3 min-w-0 w-full">
                      <div>
                        <p className="font-semibold text-foreground break-all">
                          {searchResult.domain}{' '}
                          {searchResult.available
                            ? 'is available'
                            : 'is not available'}
                        </p>
                        {searchResult.available && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground">
                              {formatCurrency(searchResult.price, {
                                currency: searchResult.currency || 'USD',
                              })}
                            </span>
                            {searchResult.isPremium && (
                              <Badge variant="warning">Premium</Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {searchResult.available && (
                        <Button
                          type="button"
                          onClick={() => setView('review')}
                          disabled={isBusy}
                        >
                          Review & purchase
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}

          {hasStore && view === 'existing' ? (
            <>
              <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
                <Paragraph className="text-sm text-muted-foreground">
                  Already own a domain? Connect it here. You’ll add TXT and
                  CNAME records at your registrar, then verify.
                </Paragraph>
              </div>

              <FormField
                label="Existing domain"
                error={formatError ?? undefined}
              >
                <Input
                  value={domainInput}
                  placeholder="store.yourbrand.com"
                  disabled={isBusy}
                  onChange={(event) => {
                    setDomainInput(event.target.value);
                    setFormatError(null);
                  }}
                />
              </FormField>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="owns-existing-domain"
                  checked={ownsExistingDomain}
                  disabled={isBusy}
                  onCheckedChange={(checked) => {
                    setOwnsExistingDomain(checked === true);
                    setFormatError(null);
                  }}
                  className="mt-0.5"
                />
                <Label
                  htmlFor="owns-existing-domain"
                  className="text-sm leading-6 font-normal text-foreground cursor-pointer"
                >
                  I already own this domain and can update its DNS records.
                </Label>
              </div>

              <Button
                type="button"
                onClick={handleContinueExisting}
                disabled={isBusy || !domainInput.trim()}
                isLoading={isConnecting}
              >
                Connect domain
              </Button>

              <button
                type="button"
                className="text-sm text-primary hover:underline w-fit text-left"
                onClick={() => {
                  setFormatError(null);
                  setOwnsExistingDomain(false);
                  setView('search');
                }}
              >
                Find a new domain instead?
              </button>
            </>
          ) : null}

          {hasStore && view === 'review' && searchResult ? (
            <>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Selected domain
                </p>
                <p className="text-xl font-bold text-foreground break-all">
                  {searchResult.domain}
                </p>
              </div>

              <div className="rounded-xl border border-border divide-y divide-border">
                <QuoteRow
                  label="Registrar price"
                  value={formatCurrency(searchResult.price, {
                    currency: searchResult.currency || 'USD',
                  })}
                />
                {searchResult.isPremium && (
                  <QuoteRow label="Type" value="Premium domain" />
                )}
              </div>

              <Paragraph className="text-sm text-muted-foreground">
                You’ll pay the Naira equivalent at Paystack checkout. Sellyt
                registers the domain after payment succeeds.
              </Paragraph>
            </>
          ) : null}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/20 gap-2 sm:gap-2">
          {!hasStore ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Got it
            </Button>
          ) : view === 'manage' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          ) : view === 'search' || view === 'existing' ? (
            customDomain ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetFlowState();
                  setView('manage');
                }}
                disabled={isBusy}
              >
                <ArrowLeft className="size-4" />
                Back
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                Close
              </Button>
            )
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setView('search')}
                disabled={isBusy}
              >
                <ArrowLeft className="size-4" />
                Back
              </Button>
              <Button
                type="button"
                onClick={handlePurchase}
                disabled={isBusy}
                isLoading={isPurchasing || isRedirecting}
              >
                <Globe2 className="size-4" />
                {isRedirecting ? 'Redirecting…' : 'Pay with Paystack'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function ManageView({
  isLoading,
  isError,
  error,
  isFetching,
  customDomain,
  verifyResult,
  isVerifying,
  isRemoving,
  onRetryLoad,
  onStartNew,
  onVerify,
  onRemove,
  onCopy,
  onOpen,
}: {
  isLoading: boolean;
  isError: boolean;
  error: Error | null | undefined;
  isFetching: boolean;
  customDomain?: CustomDomain | null;
  verifyResult: CustomDomainVerifyResult | null;
  isVerifying: boolean;
  isRemoving: boolean;
  onRetryLoad: () => void;
  onStartNew: () => void;
  onVerify: () => void;
  onRemove: () => void;
  onCopy: (value: string) => void;
  onOpen: (domain: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="size-4 animate-spin" />
        Loading domain status…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 space-y-3">
        <Paragraph className="text-sm text-destructive">
          {error?.message || 'Could not load custom domain status.'}
        </Paragraph>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetryLoad}
          isLoading={isFetching}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (!customDomain) {
    return (
      <div className="space-y-4">
        <div className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Globe2 className="size-5" />
          </div>
          <div className="space-y-2">
            <Paragraph className="font-semibold text-foreground">
              Get your own custom domain
            </Paragraph>
            <Paragraph className="text-sm text-muted-foreground">
              Buy a new domain through Sellyt, or connect one you already own.
            </Paragraph>
          </div>
        </div>
        <Button type="button" onClick={onStartNew}>
          Get a Custom Domain
        </Button>
      </div>
    );
  }

  return (
    <StatusPanel
      customDomain={customDomain}
      verifyResult={verifyResult}
      isVerifying={isVerifying}
      isRemoving={isRemoving}
      onCopy={onCopy}
      onOpen={() => onOpen(customDomain.domain)}
      onVerify={onVerify}
      onRemove={onRemove}
      onChangeDomain={onStartNew}
    />
  );
}

function StatusPanel({
  customDomain,
  verifyResult,
  isVerifying,
  isRemoving,
  onCopy,
  onOpen,
  onVerify,
  onRemove,
  onChangeDomain,
}: {
  customDomain: CustomDomain;
  verifyResult: CustomDomainVerifyResult | null;
  isVerifying: boolean;
  isRemoving: boolean;
  onCopy: (value: string) => void;
  onOpen: () => void;
  onVerify: () => void;
  onRemove: () => void;
  onChangeDomain: () => void;
}) {
  const presentation = useMemo(
    () => getCustomDomainStatusPresentation(customDomain.status),
    [customDomain.status]
  );
  const isActive = isCustomDomainActive(customDomain.status);
  const showDns = needsDnsSetup(customDomain.status);
  const records = customDomain.records ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Your domain
          </p>
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-xl font-bold text-foreground break-all">
              {customDomain.domain}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Copy Domain"
              onClick={() => onCopy(customDomain.domain)}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        </div>
        <Badge variant={presentation.badgeVariant}>{presentation.label}</Badge>
      </div>

      <Paragraph className="text-sm text-muted-foreground">
        {presentation.description}
      </Paragraph>

      {verifyResult && (
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 space-y-1">
          <Paragraph
            size="xs"
            className="font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Last verification check
          </Paragraph>
          <Paragraph size="sm" className="text-foreground">
            TXT: {verifyResult.txtVerified ? 'Verified' : 'Not verified'} ·
            CNAME: {verifyResult.cnameVerified ? 'Verified' : 'Not verified'}
          </Paragraph>
        </div>
      )}

      {showDns && records.length > 0 && (
        <div className="space-y-3">
          <Paragraph size="sm" className="font-semibold text-foreground">
            DNS records to add
          </Paragraph>
          {records.map((record) => (
            <DnsRecordCard
              key={`${record.type}-${record.name}`}
              record={record}
              onCopy={onCopy}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {isActive && (
          <Button type="button" variant="outline" onClick={onOpen}>
            <ExternalLink className="size-4" />
            Open store
          </Button>
        )}
        {showDns && (
          <Button type="button" onClick={onVerify} isLoading={isVerifying}>
            Verify DNS
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={onRemove}
          isLoading={isRemoving}
        >
          <Trash2 className="size-4" />
          Remove domain
        </Button>
        <Button type="button" variant="outline" onClick={onChangeDomain}>
          Change domain
        </Button>
      </div>
    </div>
  );
}

function DnsRecordCard({
  record,
  onCopy,
}: {
  record: DomainDnsRecord;
  onCopy: (value: string) => void;
}) {
  return (
    <div className="rounded-xl border border-border p-3 space-y-2">
      <Badge variant="info">{record.type}</Badge>
      <div className="space-y-2">
        <CopyableRow label="Name / host" value={record.name} onCopy={onCopy} />
        <CopyableRow label="Value" value={record.value} onCopy={onCopy} />
      </div>
    </div>
  );
}

function CopyableRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="flex items-start gap-2">
        <code className="flex-1 text-xs break-all rounded-md bg-muted/50 px-2 py-1.5 text-foreground">
          {value}
        </code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Copy ${label}`}
          onClick={() => onCopy(value)}
        >
          <Copy className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function QuoteRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}
